import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';

interface ScrollRevealContextType {
  registerElement: (element: Element | null) => void;
}

const ScrollRevealContext = createContext<ScrollRevealContextType | null>(null);

/**
 * Provider that creates a SINGLE IntersectionObserver shared across all components.
 * This fixes the performance issue of creating multiple observers.
 */
export const ScrollRevealProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementsRef = useRef<Set<Element>>(new Set());

  useEffect(() => {
    // Create single observer for entire app
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            // Unobserve after revealing for better performance
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    // Observe any elements that registered before observer was created
    elementsRef.current.forEach((el) => {
      observerRef.current?.observe(el);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const registerElement = useCallback((element: Element | null) => {
    if (!element) return;
    
    // Avoid re-registering
    if (elementsRef.current.has(element)) return;
    
    elementsRef.current.add(element);
    
    // If observer exists, observe immediately
    if (observerRef.current) {
      observerRef.current.observe(element);
    }
  }, []);

  return (
    <ScrollRevealContext.Provider value={{ registerElement }}>
      {children}
    </ScrollRevealContext.Provider>
  );
};

/**
 * Hook to access the shared scroll reveal functionality.
 * Must be used within ScrollRevealProvider.
 */
export const useScrollReveal = (): ScrollRevealContextType => {
  const context = useContext(ScrollRevealContext);
  
  // Fallback for components outside provider (shouldn't happen but safe)
  if (!context) {
    return {
      registerElement: () => {},
    };
  }
  
  return context;
};
